// Scheduler utility functions for event layout, date handling, and grid logic.
// All functions are pure and reusable for the scheduler UI.

import {
  addDays,
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfDay,
  endOfMonth,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
} from "date-fns"
import { toZonedTime } from "date-fns-tz"

import { TTimezone } from "src/data/timezones/timezones"

import {
  TEventSpanInfo,
  TEventWithLane,
  TEventWithSpanInfo,
  TOOLTIP_DEFAULTS,
  TSchedulerEvent,
  TSchedulerViewType,
  TTooltipPosition,
  TTooltipPositionParams,
} from "./schedulerTypes"

/**
 * Calculates optimal tooltip position based on cursor, tooltip size, and viewport.
 * Prefers right/bottom placement, falls back to left/top or center if needed.
 */
export function calculateTooltipPosition({
  mouseX,
  mouseY,
  tooltipWidth = TOOLTIP_DEFAULTS.WIDTH,
  tooltipHeight = TOOLTIP_DEFAULTS.HEIGHT,
  offset = TOOLTIP_DEFAULTS.OFFSET,
}: TTooltipPositionParams): TTooltipPosition {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  const availableSpaceRight = viewportWidth - mouseX - offset
  const availableSpaceLeft = mouseX - offset
  const availableSpaceBottom = viewportHeight - mouseY - offset
  const availableSpaceTop = mouseY - offset

  let left: number
  if (availableSpaceRight >= tooltipWidth) {
    left = mouseX + offset
  } else if (availableSpaceLeft >= tooltipWidth) {
    left = mouseX - tooltipWidth - offset
  } else {
    left = Math.max(offset, (viewportWidth - tooltipWidth) / 2)
  }

  let top: number
  if (availableSpaceBottom >= tooltipHeight) {
    top = mouseY + offset
  } else if (availableSpaceTop >= tooltipHeight) {
    top = mouseY - tooltipHeight - offset
  } else {
    top = Math.max(offset, (viewportHeight - tooltipHeight) / 2)
  }

  left = Math.max(offset, Math.min(left, viewportWidth - tooltipWidth - offset))
  top = Math.max(offset, Math.min(top, viewportHeight - tooltipHeight - offset))

  return {
    left,
    top,
    transform: "",
  }
}

/**
 * Converts all event start/end times to the specified timezone.
 */
export function convertEventsToTimezone({
  events,
  timezone,
}: {
  events: TSchedulerEvent[]
  timezone?: TTimezone["tzCode"]
}): TSchedulerEvent[] {
  if (!timezone) {
    return events
  }

  return events.map((event) => ({
    ...event,
    start: toZonedTime(event.start, timezone),
    end: toZonedTime(event.end, timezone),
  }))
}

/**
 * Returns the current time in the specified timezone.
 */
export function getCurrentTimeInTimezone({
  timezone,
}: {
  timezone?: string
}): Date {
  return timezone ? toZonedTime(new Date(), timezone) : new Date()
}

/**
 * Generates header cells (dates or months) for the scheduler grid based on view type and date range.
 */
export function generateHeaderCells({
  viewType,
  startDate,
  endDate,
  timezone,
}: {
  viewType: TSchedulerViewType
  startDate: Date
  endDate: Date
  timezone?: TTimezone["tzCode"]
}): Date[] {
  let cells: Date[]
  if (viewType === "week" || viewType === "month") {
    cells = eachDayOfInterval({ start: startDate, end: endDate })
  } else {
    cells = eachMonthOfInterval({ start: startDate, end: endDate })
  }

  // Convert header cells to timezone if specified
  if (timezone) {
    return cells.map((cell) => toZonedTime(cell, timezone))
  }

  return cells
}

/**
 * Returns all events that intersect with a specific scheduler cell (day/month) for a given resource.
 */
export function getEventsForSchedulerCell({
  events,
  resourceId,
  cellDate,
  viewType,
}: {
  events: TSchedulerEvent[]
  resourceId: string
  cellDate: Date
  viewType: TSchedulerViewType
}): TSchedulerEvent[] {
  return events.filter((event) => {
    const isSameResource = event.resourceId === resourceId
    const cellStart =
      viewType === "week" || viewType === "month"
        ? startOfDay(cellDate)
        : startOfMonth(cellDate)
    const cellEnd =
      viewType === "week" || viewType === "month"
        ? addDays(startOfDay(cellDate), 1)
        : addDays(endOfMonth(cellDate), 1)
    return isSameResource && event.start < cellEnd && event.end > cellStart
  })
}

/**
 * Calculates the span info for an event (start/end column, width, etc) in the scheduler grid.
 */
export function calculateEventSpanInfo({
  event,
  columnIndex,
  headerCells,
  viewType,
}: {
  event: TSchedulerEvent
  columnIndex: number
  headerCells: Date[]
  viewType: TSchedulerViewType
}): TEventSpanInfo {
  function findColumnIndex(date: Date): number {
    return headerCells.findIndex((cellDate) => {
      const cellStart =
        viewType === "week" || viewType === "month"
          ? startOfDay(cellDate)
          : startOfMonth(cellDate)
      const cellEnd =
        viewType === "week" || viewType === "month"
          ? endOfDay(cellDate)
          : endOfMonth(cellDate)
      return date >= cellStart && date < cellEnd
    })
  }

  let spanStartIndex = findColumnIndex(event.start)

  if (spanStartIndex === -1) {
    const firstHeaderCell = headerCells[0]
    if (
      headerCells.length > 0 &&
      firstHeaderCell !== undefined &&
      event.start < firstHeaderCell
    ) {
      spanStartIndex = 0
    } else {
      return {
        spanWidth: 0,
        isSpanStart: false,
        isSpanEnd: false,
        spanStartIndex: -1,
        spanEndIndex: -1,
      }
    }
  }

  const actualEnd = event.end
  let spanEndIndex = findColumnIndex(actualEnd)

  if (spanEndIndex === -1) {
    if (headerCells.length > 0) {
      const lastHeaderCell = headerCells[headerCells.length - 1]
      if (lastHeaderCell !== undefined && actualEnd > lastHeaderCell) {
        spanEndIndex = headerCells.length - 1
      } else {
        spanEndIndex = spanStartIndex
      }
    } else {
      spanEndIndex = spanStartIndex
    }
  }

  const spanWidth = Math.max(1, spanEndIndex - spanStartIndex + 1)
  return {
    spanWidth,
    isSpanStart: columnIndex === spanStartIndex,
    isSpanEnd: columnIndex === spanEndIndex,
    spanStartIndex,
    spanEndIndex,
  }
}

/**
 * Allocates lanes for events to avoid overlaps, considering event spans and priorities.
 * Priority events are always separated from non-priority events.
 */
export function allocateEventLanes({
  events,
  viewType,
}: {
  events: TEventWithSpanInfo[]
  viewType: TSchedulerViewType
}): TEventWithLane[] {
  if (events.length === 0) {
    return []
  }

  // Sort events for consistent lane assignment
  const sortedEvents = sortEventsForLaneAllocation(events, viewType)
  const eventLanes: TEventWithLane[] = []

  // Assign each event to the lowest available lane
  sortedEvents.forEach(({ event, spanInfo }) => {
    const assignedLane = findLowestAvailableLane(
      event,
      spanInfo,
      eventLanes,
      viewType
    )
    eventLanes.push({ event, spanInfo, lane: assignedLane })
  })

  return eventLanes
}

/**
 * Sorts events by priority, start time, and duration for consistent lane assignment
 */
function sortEventsForLaneAllocation(
  events: TEventWithSpanInfo[],
  viewType: TSchedulerViewType
): TEventWithSpanInfo[] {
  return [...events].sort((a, b) => {
    // Priority events first
    const priorityA = a.event.priority ?? Infinity
    const priorityB = b.event.priority ?? Infinity
    if (priorityA !== priorityB) {
      return priorityA - priorityB
    }

    // For week/month view: prioritize events that span multiple days
    if (viewType === "week" || viewType === "month") {
      const aSpanWidth = a.spanInfo.spanWidth
      const bSpanWidth = b.spanInfo.spanWidth
      if (aSpanWidth !== bSpanWidth) {
        return bSpanWidth - aSpanWidth // Longer spans first
      }
    }

    // For year view: longer events first
    if (viewType === "year") {
      const aDuration = a.event.end.getTime() - a.event.start.getTime()
      const bDuration = b.event.end.getTime() - b.event.start.getTime()
      if (aDuration !== bDuration) {
        return bDuration - aDuration
      }
    }

    // Then by start time
    const startComparison = a.event.start.getTime() - b.event.start.getTime()
    if (startComparison !== 0) {
      return startComparison
    }

    // Finally by duration (longer events first)
    const aDuration = a.event.end.getTime() - a.event.start.getTime()
    const bDuration = b.event.end.getTime() - b.event.start.getTime()
    return bDuration - aDuration
  })
}

/**
 * Finds the lowest available lane for an event
 */
function findLowestAvailableLane(
  event: TSchedulerEvent,
  spanInfo: TEventSpanInfo,
  existingLanes: TEventWithLane[],
  viewType: TSchedulerViewType
): number {
  // Find all used lanes
  const usedLanes = new Set(existingLanes.map((el) => el.lane))

  // Check if there are unused lanes available
  const hasUnusedLanes = usedLanes.size > 0 && Math.max(...usedLanes) > 0

  let lane = 0

  // Keep trying lanes until we find an available one
  while (
    !canShareLane(
      event,
      spanInfo,
      existingLanes.filter((el) => el.lane === lane),
      viewType,
      hasUnusedLanes
    )
  ) {
    lane++
  }

  return lane
}

/**
 * Checks if an event can share a lane with existing events
 */
function canShareLane(
  event: TSchedulerEvent,
  spanInfo: TEventSpanInfo,
  eventsInLane: TEventWithLane[],
  viewType: TSchedulerViewType,
  hasUnusedLanes: boolean = false
): boolean {
  // Check priority compatibility
  if (!hasCompatiblePriority(event, eventsInLane)) {
    return false
  }

  // If there are unused lanes available, be more aggressive about preventing overlaps
  if (hasUnusedLanes) {
    // Check for any day range overlap, not just time overlap
    return !eventsInLane.some(({ event: _, spanInfo: otherSpanInfo }) => {
      const dayRangesOverlap =
        spanInfo.spanStartIndex <= otherSpanInfo.spanEndIndex &&
        otherSpanInfo.spanStartIndex <= spanInfo.spanEndIndex
      return dayRangesOverlap
    })
  }

  // Check for conflicts with existing events (time-based overlap)
  return !eventsInLane.some(({ event: otherEvent, spanInfo: otherSpanInfo }) =>
    hasConflict(event, spanInfo, otherEvent, otherSpanInfo, viewType)
  )
}

/**
 * Checks if event priorities are compatible
 */
function hasCompatiblePriority(
  event: TSchedulerEvent,
  eventsInLane: TEventWithLane[]
): boolean {
  const eventHasPriority = event.priority !== undefined
  const laneHasPriorityEvent = eventsInLane.some(
    (el) => el.event.priority !== undefined
  )
  const laneHasNonPriorityEvent = eventsInLane.some(
    (el) => el.event.priority === undefined
  )

  // Priority and non-priority events cannot share lanes
  return !(
    (laneHasPriorityEvent && !eventHasPriority) ||
    (laneHasNonPriorityEvent && eventHasPriority)
  )
}

/**
 * Checks if two events conflict with each other
 */
function hasConflict(
  event1: TSchedulerEvent,
  spanInfo1: TEventSpanInfo,
  event2: TSchedulerEvent,
  spanInfo2: TEventSpanInfo,
  viewType: TSchedulerViewType
): boolean {
  // Check if day ranges overlap
  const dayRangesOverlap =
    spanInfo1.spanStartIndex <= spanInfo2.spanEndIndex &&
    spanInfo2.spanStartIndex <= spanInfo1.spanEndIndex

  if (!dayRangesOverlap) {
    return false // No overlap, no conflict
  }

  // View-specific conflict detection
  if (viewType === "year") {
    // Year view: overlapping day ranges = conflict
    return true
  } else {
    // Week/Month view: check for actual time overlap when day ranges overlap
    // This prevents unnecessary lane skipping while still preventing conflicts
    const timeOverlap = event1.start < event2.end && event2.start < event1.end

    // If there's no time overlap, events can share the same lane
    // This allows for more efficient lane usage
    return timeOverlap
  }
}

/**
 * Returns true if the given date is the current day/month/year for the scheduler grid.
 * Uses grid options to determine highlight logic.
 */
export function isDateCurrent({
  date,
  now,
  viewType,
}: {
  date: Date
  now: Date
  viewType: TSchedulerViewType
}): boolean {
  const isCurrentPeriod =
    viewType === "week" || viewType === "month"
      ? isSameDay(date, now)
      : isSameMonth(date, now)
  return isCurrentPeriod
}

/**
 * Returns event colors, falling back to defaults if not provided.
 */
export function getEventColors({
  event,
  defaultColors,
}: {
  event: TSchedulerEvent
  defaultColors: {
    backgroundColor: string
    textColor: string
  }
}): {
  backgroundColor: string
  textColor: string
  borderColor: string
} {
  return {
    backgroundColor: event.fillColor || defaultColors.backgroundColor,
    textColor: event.color || defaultColors.textColor,
    borderColor: event.borderColor || "none",
  }
}
